import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCallback, useState, useEffect } from "react";
import { Circle, Loader2, Copy, ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  startRecordingMeetingApi,
  stopRecordingMeetingApi,
  getRecordingsMeetingApi,
  updateRecordingLayoutApi,
} from "@/modules/meetings/api/api-meeting";
import {
  Meeting,
  RecordingInfo,
} from "@/modules/meetings/types/meeting.type";

interface RecordSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: Meeting | null;
  isHost: boolean;
}

export const RecordSheet: React.FC<RecordSheetProps> = ({
  open,
  onOpenChange,
  meeting,
  isHost,
}) => {
  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [isRecordingLoading, setIsRecordingLoading] = useState(false);
  const [recordingEgressId, setRecordingEgressId] = useState<string | null>(
    null
  );
  const [activeRecording, setActiveRecording] = useState<RecordingInfo | null>(
    null
  );
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [recordings, setRecordings] = useState<RecordingInfo[]>([]);
  const [isLoadingRecordings, setIsLoadingRecordings] = useState(false);
  const [stoppingIds, setStoppingIds] = useState<Set<string>>(new Set());
  const [updatingLayoutIds, setUpdatingLayoutIds] = useState<Set<string>>(new Set());
  const [currentLayout, setCurrentLayout] = useState<string>("grid");

  // Recording form states
  const [showRecordForm, setShowRecordForm] = useState(false);
  const [layout, setLayout] = useState<string>("grid");
  const [audioMixing, setAudioMixing] = useState<string>("DEFAULT_MIXING");
  const [fileType, setFileType] = useState<string>("mp4");
  const [encodingPreset, setEncodingPreset] = useState<string>("");
  const [fileNamePrefix, setFileNamePrefix] = useState<string>("");
  const [s3Folder, setS3Folder] = useState<string>("");
  const [audioOnly, setAudioOnly] = useState<boolean>(false);
  const [videoOnly, setVideoOnly] = useState<boolean>(false);
  const [showCustomEncoding, setShowCustomEncoding] = useState<boolean>(false);
  const [customEncoding, setCustomEncoding] = useState<{
    width?: number;
    height?: number;
    bitrate?: number;
    frameRate?: number;
    videoBitrate?: number;
    audioBitrate?: number;
    audioSampleRate?: number;
    audioFrequency?: number;
    keyFrameInterval?: number;
  }>({});

  // Format thời gian (HH:MM:SS)
  const formatDuration = useCallback((seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Fetch recordings
  const fetchRecordings = useCallback(async () => {
    if (!meeting?.id) return;

    try {
      setIsLoadingRecordings(true);
      const response = await getRecordingsMeetingApi(meeting.id);

      let recordingsData: RecordingInfo[] = [];
      if (response?.data?.data) {
        if (Array.isArray(response.data.data)) {
          recordingsData = response.data.data;
        } else if (
          response.data.data.recordings &&
          Array.isArray(response.data.data.recordings)
        ) {
          recordingsData = response.data.data.recordings;
        }
      } else if (Array.isArray(response?.data)) {
        recordingsData = response.data;
      }

      setRecordings(recordingsData);

      // Tìm recording đang chạy
      const active = recordingsData.find(
        (rec) => rec.status === "recording" && !rec.endedAt && rec.startedAt
      );
      console.log(active);

      if (active && active.startedAt) {
        setActiveRecording(active);
        setIsRecording(true);
        setRecordingEgressId(active.egressId);
        if (active.layout) {
          setCurrentLayout(active.layout);
        }
      } else {
        setActiveRecording(null);
        if (!isRecordingLoading) {
          setIsRecording(false);
        }
      }
    } catch (error) {
      console.error("Error fetching recordings:", error);
    } finally {
      setIsLoadingRecordings(false);
    }
  }, [meeting?.id, isRecordingLoading]);

  // Tính toán thời gian đã record
  useEffect(() => {
    if (!activeRecording?.startedAt) {
      setRecordingDuration(0);
      return;
    }

    const updateDuration = () => {
      const startTime = new Date(activeRecording.startedAt).getTime();
      const now = Date.now();
      const duration = Math.floor((now - startTime) / 1000);
      setRecordingDuration(duration);
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [activeRecording?.startedAt]);

  // Fetch khi sheet mở
  useEffect(() => {
    if (open && meeting?.id) {
      fetchRecordings();
    }
  }, [open, meeting?.id, fetchRecordings]);

  // Handle start recording
  const handleStartRecording = useCallback(
    async (
      layout: string,
      audioMixing: string,
      fileType: string,
      encodingPreset?: string,
      customEncoding?: {
        width?: number;
        height?: number;
        bitrate?: number;
        frameRate?: number;
        videoBitrate?: number;
        audioBitrate?: number;
        audioSampleRate?: number;
        audioFrequency?: number;
        keyFrameInterval?: number;
      },
      audioOnly?: boolean,
      videoOnly?: boolean,
      fileNamePrefix?: string,
      s3Folder?: string
    ) => {
      if (!meeting || !meeting.id) {
        toast.error("Không tìm thấy thông tin cuộc họp");
        return;
      }

      if (!meeting.allowRecording) {
        toast.error("Cuộc họp này không cho phép ghi lại");
        return;
      }

      try {
        setIsRecordingLoading(true);

        const payload: {
          layout?: string;
          encodingPreset?: string;
          customEncoding?: {
            width?: number;
            height?: number;
            bitrate?: number;
            frameRate?: number;
            videoBitrate?: number;
            audioBitrate?: number;
            audioSampleRate?: number;
            audioFrequency?: number;
            keyFrameInterval?: number;
          };
          audioOnly?: boolean;
          videoOnly?: boolean;
          audioMixing?: string;
          fileType?: string;
          fileNamePrefix?: string;
          s3Folder?: string;
        } = {};

        if (layout) payload.layout = layout;
        if (encodingPreset && encodingPreset !== "none") payload.encodingPreset = encodingPreset;
        if (customEncoding && Object.keys(customEncoding).length > 0) {
          payload.customEncoding = customEncoding;
        }
        if (audioOnly) payload.audioOnly = audioOnly;
        if (videoOnly) payload.videoOnly = videoOnly;
        if (audioMixing) payload.audioMixing = audioMixing;
        if (fileType) payload.fileType = fileType;
        if (fileNamePrefix) payload.fileNamePrefix = fileNamePrefix;
        if (s3Folder) payload.s3Folder = s3Folder;

        const response = await startRecordingMeetingApi(meeting.id, payload);

        let egressId: string | null = null;
        if (response?.data?.data?.egressId) {
          egressId = response.data.data.egressId;
        } else if (response?.data?.egressId) {
          egressId = response.data.egressId;
        }

        if (egressId) {
          setRecordingEgressId(egressId);
        }

        setIsRecording(true);
        setShowRecordForm(false);
        setEncodingPreset("");
        setFileNamePrefix("");
        setS3Folder("");
        setAudioOnly(false);
        setVideoOnly(false);
        setShowCustomEncoding(false);
        setCustomEncoding({});
        toast.success("Đã bắt đầu ghi lại cuộc họp");
        setTimeout(() => {
          fetchRecordings();
        }, 1000);
      } catch (error: unknown) {
        console.error("Error starting recording:", error);
        const errorMessage =
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message || "Đã xảy ra lỗi khi ghi lại cuộc họp";
        toast.error(errorMessage);
        throw error;
      } finally {
        setIsRecordingLoading(false);
      }
    },
    [meeting, fetchRecordings]
  );

  // Handle stop recording
  const handleStopRecording = useCallback(
    async (egressId: string) => {
      if (!meeting?.id) return;

      try {
        setStoppingIds((prev) => new Set(prev).add(egressId));
        await stopRecordingMeetingApi(meeting.id, { egressId });
        setIsRecording(false);
        setRecordingEgressId(null);
        setActiveRecording(null);
        setRecordingDuration(0);
        toast.success("Đã dừng ghi lại cuộc họp");
        await fetchRecordings();
      } catch (error: unknown) {
        console.error("Error stopping recording:", error);
        const errorMessage =
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message || "Đã xảy ra lỗi khi dừng ghi lại";
        toast.error(errorMessage);
      } finally {
        setStoppingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(egressId);
          return newSet;
        });
      }
    },
    [meeting?.id, fetchRecordings]
  );

  // Handle update recording layout
  const handleUpdateRecordingLayout = useCallback(
    async (egressId: string, newLayout: string) => {
      if (!meeting?.id) return;

      try {
        setUpdatingLayoutIds((prev) => new Set(prev).add(egressId));
        await updateRecordingLayoutApi(meeting.id, {
          egressId,
          layout: newLayout,
        });
        setCurrentLayout(newLayout);
        toast.success("Đã cập nhật bố cục");
        await fetchRecordings();
      } catch (error: unknown) {
        console.error("Error updating recording layout:", error);
        const errorMessage =
          (error as { data?: { error?: { message?: string } } })?.data?.error
            ?.message || "Đã xảy ra lỗi khi cập nhật bố cục";
        toast.error(errorMessage);
      } finally {
        setUpdatingLayoutIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(egressId);
          return newSet;
        });
      }
    },
    [meeting?.id, fetchRecordings]
  );

  // Handle submit record form
  const handleSubmitRecord = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const customEnc = showCustomEncoding && Object.keys(customEncoding).length > 0
        ? {
          ...(customEncoding.width && { width: Number(customEncoding.width) }),
          ...(customEncoding.height && { height: Number(customEncoding.height) }),
          ...(customEncoding.bitrate && { bitrate: Number(customEncoding.bitrate) }),
          ...(customEncoding.frameRate && { frameRate: Number(customEncoding.frameRate) }),
          ...(customEncoding.videoBitrate && { videoBitrate: Number(customEncoding.videoBitrate) }),
          ...(customEncoding.audioBitrate && { audioBitrate: Number(customEncoding.audioBitrate) }),
          ...(customEncoding.audioSampleRate && { audioSampleRate: Number(customEncoding.audioSampleRate) }),
          ...(customEncoding.audioFrequency && { audioFrequency: Number(customEncoding.audioFrequency) }),
          ...(customEncoding.keyFrameInterval && { keyFrameInterval: Number(customEncoding.keyFrameInterval) }),
        }
        : undefined;

      await handleStartRecording(
        layout,
        audioMixing,
        fileType,
        encodingPreset || undefined,
        customEnc,
        audioOnly,
        videoOnly,
        fileNamePrefix || undefined,
        s3Folder || undefined
      );
    },
    [
      layout,
      audioMixing,
      fileType,
      encodingPreset,
      customEncoding,
      showCustomEncoding,
      audioOnly,
      videoOnly,
      fileNamePrefix,
      s3Folder,
      handleStartRecording,
    ]
  );

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Đã copy vào clipboard");
  }, []);

  if (!isHost) {
    return null;
  }

  if (!meeting?.allowRecording) {
    return null;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Ghi lại cuộc họp</SheetTitle>
          <SheetDescription>Quản lý ghi lại cuộc họp</SheetDescription>
        </SheetHeader>

        <div className="space-y-6 p-4">
          {/* Recording Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Circle className="size-5" />
                Ghi lại
              </h3>
              {isRecording && (
                <span className="text-sm font-medium text-red-600">
                  {formatDuration(recordingDuration)}
                </span>
              )}
            </div>

            <div className="space-y-3">
              {!isRecording && !showRecordForm && (
                <Button
                  variant="default"
                  onClick={() => setShowRecordForm(true)}
                  className="w-full"
                  aria-label="Bắt đầu ghi lại"
                >
                  <Circle className="size-4 mr-2" />
                  Bắt đầu ghi lại
                </Button>
              )}

              {showRecordForm && !isRecording && (
                <form onSubmit={handleSubmitRecord} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="layout">Bố cục video</Label>
                    <Select
                      value={layout}
                      onValueChange={setLayout}
                      disabled={isRecordingLoading}
                    >
                      <SelectTrigger id="layout">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="speaker">Người thuyết trình</SelectItem>
                        <SelectItem value="grid">Dạng lưới</SelectItem>
                        <SelectItem value="single-speaker">
                          Một người thuyết trình
                        </SelectItem>
                        <SelectItem value="speaker-dark">
                          Người thuyết trình (nền tối)
                        </SelectItem>
                        <SelectItem value="grid-dark">Dạng lưới (nền tối)</SelectItem>
                        <SelectItem value="single-speaker-dark">
                          Một người thuyết trình (nền tối)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="audioMixing">Audio Mixing</Label>
                    <Select
                      value={audioMixing}
                      onValueChange={setAudioMixing}
                      disabled={isRecordingLoading}
                    >
                      <SelectTrigger id="audioMixing">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DEFAULT_MIXING">
                          Default Mixing
                        </SelectItem>
                        <SelectItem value="MICROPHONE_ONLY">
                          Microphone Only
                        </SelectItem>
                        <SelectItem value="SPEAKER_ONLY">
                          Speaker Only
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fileType">File Type</Label>
                    <Select
                      value={fileType}
                      onValueChange={setFileType}
                      disabled={isRecordingLoading}
                    >
                      <SelectTrigger id="fileType">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mp4">MP4</SelectItem>
                        <SelectItem value="webm">WebM</SelectItem>
                        <SelectItem value="mp3">MP3</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="encodingPreset">Encoding Preset</Label>
                    <Select
                      value={encodingPreset}
                      onValueChange={setEncodingPreset}
                      disabled={isRecordingLoading}
                    >
                      <SelectTrigger id="encodingPreset">
                        <SelectValue placeholder="Chọn preset (tùy chọn)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Không sử dụng preset</SelectItem>
                        <SelectItem value="H264_720P_30">H264 720P 30fps</SelectItem>
                        <SelectItem value="H264_1080P_30">H264 1080P 30fps</SelectItem>
                        <SelectItem value="H264_1080P_60">H264 1080P 60fps</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fileNamePrefix">File Name Prefix</Label>
                    <Input
                      id="fileNamePrefix"
                      type="text"
                      value={fileNamePrefix}
                      onChange={(e) => setFileNamePrefix(e.target.value)}
                      placeholder="recording_ (tùy chọn)"
                      disabled={isRecordingLoading}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="s3Folder">S3 Folder</Label>
                    <Input
                      id="s3Folder"
                      type="text"
                      value={s3Folder}
                      onChange={(e) => setS3Folder(e.target.value)}
                      placeholder="recordings/ (tùy chọn)"
                      disabled={isRecordingLoading}
                    />
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="audioOnly"
                        checked={audioOnly}
                        onCheckedChange={(checked) => {
                          setAudioOnly(checked === true);
                          if (checked) setVideoOnly(false);
                        }}
                        disabled={isRecordingLoading}
                      />
                      <Label
                        htmlFor="audioOnly"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Chỉ audio
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="videoOnly"
                        checked={videoOnly}
                        onCheckedChange={(checked) => {
                          setVideoOnly(checked === true);
                          if (checked) setAudioOnly(false);
                        }}
                        disabled={isRecordingLoading}
                      />
                      <Label
                        htmlFor="videoOnly"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Chỉ video
                      </Label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setShowCustomEncoding(!showCustomEncoding)}
                      className="flex items-center justify-between w-full p-2 text-sm font-medium text-left border rounded-lg hover:bg-accent"
                      disabled={isRecordingLoading}
                    >
                      <span>Custom Encoding (Tùy chọn)</span>
                      {showCustomEncoding ? (
                        <ChevronUp className="size-4" />
                      ) : (
                        <ChevronDown className="size-4" />
                      )}
                    </button>
                    {showCustomEncoding && (
                      <div className="space-y-3 p-3 border rounded-lg bg-muted/50">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-2">
                            <Label htmlFor="width" className="text-xs">
                              Width
                            </Label>
                            <Input
                              id="width"
                              type="number"
                              value={customEncoding.width || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  width: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="1920"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="height" className="text-xs">
                              Height
                            </Label>
                            <Input
                              id="height"
                              type="number"
                              value={customEncoding.height || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  height: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="1080"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="frameRate" className="text-xs">
                              Frame Rate
                            </Label>
                            <Input
                              id="frameRate"
                              type="number"
                              value={customEncoding.frameRate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  frameRate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="30"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="bitrate" className="text-xs">
                              Bitrate (kbps)
                            </Label>
                            <Input
                              id="bitrate"
                              type="number"
                              value={customEncoding.bitrate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  bitrate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="2500"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="videoBitrate" className="text-xs">
                              Video Bitrate (kbps)
                            </Label>
                            <Input
                              id="videoBitrate"
                              type="number"
                              value={customEncoding.videoBitrate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  videoBitrate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="2000"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="audioBitrate" className="text-xs">
                              Audio Bitrate (kbps)
                            </Label>
                            <Input
                              id="audioBitrate"
                              type="number"
                              value={customEncoding.audioBitrate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  audioBitrate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="128"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="audioSampleRate" className="text-xs">
                              Audio Sample Rate (Hz)
                            </Label>
                            <Input
                              id="audioSampleRate"
                              type="number"
                              value={customEncoding.audioSampleRate || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  audioSampleRate: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="48000"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="audioFrequency" className="text-xs">
                              Audio Frequency (Hz)
                            </Label>
                            <Input
                              id="audioFrequency"
                              type="number"
                              value={customEncoding.audioFrequency || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  audioFrequency: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="48000"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                          <div className="space-y-2 col-span-2">
                            <Label htmlFor="keyFrameInterval" className="text-xs">
                              Key Frame Interval
                            </Label>
                            <Input
                              id="keyFrameInterval"
                              type="number"
                              value={customEncoding.keyFrameInterval || ""}
                              onChange={(e) =>
                                setCustomEncoding({
                                  ...customEncoding,
                                  keyFrameInterval: e.target.value
                                    ? Number(e.target.value)
                                    : undefined,
                                })
                              }
                              placeholder="2"
                              disabled={isRecordingLoading}
                              className="h-8"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowRecordForm(false);
                        setEncodingPreset("");
                        setFileNamePrefix("");
                        setS3Folder("");
                        setAudioOnly(false);
                        setVideoOnly(false);
                        setShowCustomEncoding(false);
                        setCustomEncoding({});
                      }}
                      disabled={isRecordingLoading}
                      className="flex-1"
                    >
                      Hủy
                    </Button>
                    <Button
                      type="submit"
                      disabled={isRecordingLoading}
                      className="flex-1"
                    >
                      {isRecordingLoading ? (
                        <>
                          <Loader2 className="size-4 mr-2 animate-spin" />
                          Đang bắt đầu...
                        </>
                      ) : (
                        "Bắt đầu ghi lại"
                      )}
                    </Button>
                  </div>
                </form>
              )}

              {isRecording && recordingEgressId && (
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="updateLayout">Cập nhật bố cục</Label>
                    <Select
                      value={currentLayout}
                      onValueChange={(value) =>
                        handleUpdateRecordingLayout(recordingEgressId, value)
                      }
                      disabled={updatingLayoutIds.has(recordingEgressId)}
                    >
                      <SelectTrigger id="updateLayout">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="speaker">Người thuyết trình</SelectItem>
                        <SelectItem value="grid">Dạng lưới</SelectItem>
                        <SelectItem value="single-speaker">
                          Một người thuyết trình
                        </SelectItem>
                        <SelectItem value="speaker-dark">
                          Người thuyết trình (nền tối)
                        </SelectItem>
                        <SelectItem value="grid-dark">Dạng lưới (nền tối)</SelectItem>
                        <SelectItem value="single-speaker-dark">
                          Một người thuyết trình (nền tối)
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="destructive"
                    onClick={() => handleStopRecording(recordingEgressId)}
                    disabled={stoppingIds.has(recordingEgressId)}
                    className="w-full"
                    aria-label="Dừng ghi lại"
                  >
                    {stoppingIds.has(recordingEgressId) ? (
                      <Loader2 className="size-4 mr-2 animate-spin" />
                    ) : (
                      <Circle
                        className={cn("size-4 mr-2", "fill-current")}
                      />
                    )}
                    Dừng ghi lại
                  </Button>
                </div>
              )}

              {isLoadingRecordings ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="size-5 animate-spin text-muted-foreground" />
                </div>
              ) : recordings.length > 0 ? (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Danh sách recordings ({recordings.length})
                  </Label>
                  <div className="space-y-2 overflow-y-auto">
                    {recordings.map((recording) => {
                      const isActive =
                        recording.egressId === activeRecording?.egressId;
                      const isCompleted = recording.endedAt !== null;
                      const hasError = recording.errorMessage !== null;
                      const isStopping = stoppingIds.has(recording.egressId);
                      return (
                        <div
                          key={recording.id || recording.egressId}
                          className={cn(
                            "p-3 border rounded-lg text-sm",
                            isActive &&
                            "bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800",
                            hasError &&
                            "bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800"
                          )}
                        >
                          <div className="space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                {recording.fileName ? (
                                  <p
                                    className="font-medium truncate"
                                    title={recording.fileName}
                                  >
                                    {recording.fileName}
                                  </p>
                                ) : (
                                  <p
                                    className="font-medium truncate"
                                    title={recording.egressId}
                                  >
                                    {recording.egressId.slice(0, 16)}...
                                  </p>
                                )}
                                {recording.startedAt && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Bắt đầu:{" "}
                                    {new Date(
                                      recording.startedAt
                                    ).toLocaleString("vi-VN")}
                                  </p>
                                )}
                                {recording.endedAt && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Kết thúc:{" "}
                                    {new Date(
                                      recording.endedAt
                                    ).toLocaleString("vi-VN")}
                                  </p>
                                )}
                                {isActive && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Đang ghi:{" "}
                                    {formatDuration(recordingDuration)}
                                  </p>
                                )}
                                {recording.duration !== null &&
                                  recording.duration > 0 && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Thời lượng:{" "}
                                      {formatDuration(recording.duration)}
                                    </p>
                                  )}
                                {recording.fileSize !== null &&
                                  recording.fileSize > 0 && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Dung lượng:{" "}
                                      {(
                                        recording.fileSize
                                      ).toFixed(2)}{" "}
                                      MB
                                    </p>
                                  )}
                                {hasError && recording.errorMessage && (
                                  <p
                                    className="text-xs text-orange-600 mt-1"
                                    title={recording.errorMessage}
                                  >
                                    Lỗi: {recording.errorMessage}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {recording.status && (
                                  <span
                                    className={cn(
                                      "text-xs px-2 py-0.5 rounded-full shrink-0",
                                      isActive
                                        ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                        : isCompleted
                                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                          : hasError
                                            ? "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200"
                                            : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                                    )}
                                  >
                                    {recording.status}
                                  </span>
                                )}
                                {isActive && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() =>
                                      handleStopRecording(recording.egressId)
                                    }
                                    disabled={isStopping}
                                    className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                    aria-label={`Dừng recording ${recording.egressId}`}
                                  >
                                    {isStopping ? (
                                      <Loader2 className="size-4 animate-spin" />
                                    ) : (
                                      <Circle className="size-4 fill-current" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </div>
                            {recording.s3Url && (
                              <div className="flex items-center gap-2 pt-1 border-t">
                                <p
                                  className="text-xs text-muted-foreground font-mono truncate flex-1"
                                  title={recording.s3Url}
                                >
                                  {recording.s3Url}
                                </p>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() =>
                                    copyToClipboard(recording.s3Url)
                                  }
                                  className="h-6 w-6 p-0 shrink-0"
                                  aria-label="Copy URL"
                                >
                                  <Copy className="size-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

